import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  RadioButtonUnchecked, // Not Proficient
  RadioButtonChecked, // Proficient
  Tonality, // Half Proficient
} from "@mui/icons-material";
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Skill,
  RollPlus,
  CharacterSheetInterface,
  RolledValue,
  Character
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import Roller from "@dndComponents/other/Roller";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import FlexBox from '@components/layout/FlexBox';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import { ABILITY_SCORES, ONES_0_to_50, PROFICIENCY_OPTIONS } from '@dndModels/Constants';
import { AbilityScoreType } from '@dndModels/Types';

interface Props {
  char: CharacterSheetInterface;
  skill: Skill;
}

const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterSkillInput({
  char,
  skill,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [bonuses_string, setBonusesString] = useState("");
  const [proficiency_multiplier, setProficiencyMultiplier] = useState(0);
  const [rolls, setRolls] = useState<RollPlus[]>([]);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [open, setOpen] = useState(false);
  const [ping, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    setTimeout(() => {
      const { char_roll, skill_rolls: new_rolls, bonuses_string: new_bonuses_string } = char_util.skillRolls(char, skill);
      setBonusesString(new_bonuses_string);
      if (char_roll) {
        setProficiencyMultiplier(char_roll.proficiency);
      }
      setRolls(new_rolls);
    }, 100);
  }, [char, skill]);

  useEffect(() => {
    char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    load();
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
      load();
    }
  }, [reloading, load]);

  const recalculated = () => {
    // console.log("Proficiencies");
    setReloading(true);
  }
  
  const renderExtras = () => {
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { popoverAnchorEl !== null && renderRoll() }
        </Popover>
        <ModalOrDrawer 
          open={open} 
          onClose={() => { 
            setOpen(false); 
            char.subscription_holder.modalClosed();
          }}>
          { renderDetails() }
        </ModalOrDrawer>
      </>
    );
  }

  const renderDetails = () => {
    const { char_roll } = char_util.skillRolls(char, skill);
    const customizations = char.customizations;
    
    return (
      <FlexBox direction="column">
        <FlexBox className="header centered">{ skill.name }</FlexBox>
        <FlexBox><DisplayStringWithReplacements display_string={ skill.description } /></FlexBox>
        <FlexBox className="header">Current { char_roll?.modifier??0 }</FlexBox>
        { char_roll && 
          <FlexBox className="small_label gray">
            { char_roll.proficiency === 1 ? 
              "Proficient" 
            : char_roll.proficiency === 2 ? 
              "Expert" 
            : char_roll.proficiency === 0.5 ? 
              "Half Proficient" : ""
            }
          </FlexBox>
        }
        { customizations && customizations.prof_overrides[skill._id] && Object.keys(customizations.prof_overrides[skill._id]).length > 0 &&
          <FlexBox className="small_label gray">(Overridden)</FlexBox>
        }
        { customizations && 
          <>
            <FlexBox xs_expand={0} className="header centered">Customizations for { skill.name }</FlexBox>
            <FlexBox>
              <SelectStringBox
                name="Proficiency Level"
                value={ customizations.prof_overrides[skill._id] && customizations.prof_overrides[skill._id].prof_level !== undefined ? `${customizations.prof_overrides[skill._id].prof_level}` : "Don't Override" }
                options={PROFICIENCY_OPTIONS}
                extra_options={["Don't Override"]}
                onChange={(changed: string) => {
                  if (changed === "Don't Override") {
                    delete customizations.prof_overrides[skill._id].prof_level;
                  } else {
                    if (!customizations.prof_overrides[skill._id]) {
                      customizations.prof_overrides[skill._id] = {};
                    }
                    customizations.prof_overrides[skill._id].prof_level = +changed;
                  }
                  setPing(!ping);
                  char.changed();
                  char.subscription_holder.recalculate();
                }}
              />
            </FlexBox>
            <FlexBox>
              <SelectStringBox
                name="Use Ability Score"
                value={ customizations.prof_overrides[skill._id] && customizations.prof_overrides[skill._id].stat_override !== undefined ? customizations.prof_overrides[skill._id].stat_override : "Don't Override" }
                options={ABILITY_SCORES}
                extra_options={["Don't Override"]}
                onChange={(changed: AbilityScoreType | "Don't Override") => {
                  if (changed === "Don't Override") {
                    delete customizations.prof_overrides[skill._id].stat_override;
                  } else {
                    if (!customizations.prof_overrides[skill._id]) {
                      customizations.prof_overrides[skill._id] = {};
                    }
                    customizations.prof_overrides[skill._id].stat_override = changed;
                  }
                  setPing(!ping);
                  char.changed();
                  char.subscription_holder.recalculate();
                }}
              />
            </FlexBox>
            <FlexBox>
              <SelectStringBox
                name="Override"
                value={ customizations.prof_overrides[skill._id] && customizations.prof_overrides[skill._id].override !== undefined ? `${customizations.prof_overrides[skill._id].override}` : "Don't Override" }
                options={["-5","-4","-3","-2","-1",...ONES_0_to_50]}
                extra_options={["Don't Override"]}
                onChange={(changed: string) => {
                  if (changed === "Don't Override") {
                    delete customizations.prof_overrides[skill._id].override;
                  } else {
                    if (!customizations.prof_overrides[skill._id]) {
                      customizations.prof_overrides[skill._id] = {};
                    }
                    customizations.prof_overrides[skill._id].override = +changed;
                  }
                  setPing(!ping);
                  char.changed();
                  char.subscription_holder.recalculate();
                }}
              />
            </FlexBox>
            <FlexBox>
              <SelectStringBox
                name="Bonus"
                value={ customizations.prof_overrides[skill._id] && customizations.prof_overrides[skill._id].bonus !== undefined ? `${customizations.prof_overrides[skill._id].bonus}` : "Don't Override" }
                options={["-5","-4","-3","-2","-1",...ONES_0_to_50]}
                extra_options={["Don't Override"]}
                onChange={(changed: string) => {
                  if (changed === "Don't Override") {
                    delete customizations.prof_overrides[skill._id].bonus;
                  } else {
                    if (!customizations.prof_overrides[skill._id]) {
                      customizations.prof_overrides[skill._id] = {};
                    }
                    customizations.prof_overrides[skill._id].bonus = +changed;
                  }
                  setPing(!ping);
                  char.changed();
                  char.subscription_holder.recalculate();
                }}
              />
            </FlexBox>
          </>
        }
      </FlexBox>
    );
  }

  const renderRoll = () => {
    return (
      <Roller 
        name={skill.name}
        rolled_values={roll}
        type="Skill Check" 
      />
    );
  }

  const minimum_roll = char_util.getMinimumRoll(char, "Ability Check", skill);
  let use_ability_score = skill.use_ability_score;
  const override_feature = char.mind.skill_ability_overrides.find(o => o.skill_ids.includes(skill._id));
  if (override_feature) {
    use_ability_score = override_feature.ability_score;
  }
  const { advantage, advantages, greater_advantage } = char_util.getAdvantage(char, "Ability Check", skill);
  return (
    <>
      <div className="curved_border" style={{
          padding: "4px"
        }}>
        <Grid container spacing={0} direction="row">
          <Grid size={{ xs: 1 }}>
            <div style={{ 
              display: "flex",
              alignItems: "center",
              height: "100%"
            }}>
              { proficiency_multiplier >= 1 ? <RadioButtonChecked /> : (proficiency_multiplier === 0.5 ? <Tonality /> : <RadioButtonUnchecked />) }
            </div>
          </Grid>
          <Grid
            size={{
              xs: 1
            }}>
            <div style={{ 
              float: "left", 
              lineHeight: "1.6" 
            }}>
              { proficiency_multiplier === 2 ? "x2" : "" }
            </div>
          </Grid>
          <Grid
            container
            spacing={0}
            direction="column"
            className="clickable"
            onClick={() => {
              setOpen(true);
            }}
            size={{
              xs: 7
            }}>
            <Grid>
              <div style={{ float: "left", lineHeight: "1.6" }}>
                { skill.name } <span style={{ color: "gray", fontSize: "11px" }}>{ use_ability_score }</span>
              </div>
            </Grid>
            { minimum_roll > 0 && 
              <Grid style={{ color: "gray", fontSize: "11px" }}>
                Minimum { minimum_roll }
              </Grid>
            }
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <DandDButtonBox 
              name={bonuses_string}
              advantage={ advantage }
              advantages={ advantages }
              greater_advantage={ greater_advantage }
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Ability Check")) && use_ability_score !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(use_ability_score))).length > 0;
                const new_rolls = char_util.rollD20({ char: char, rolls, type: "Ability Check",  use_ability_score, advantage, greater_advantage, minimum_roll });
                console.log(new_rolls);
                setRoll(new_rolls);
                setPopoverAnchorEl(event.currentTarget);
              }} 
            />
          </Grid>
        </Grid>
      </div>
      { renderExtras() }
    </>
  );
}
